import React, { useMemo } from 'react';
import { VictoryPie, VictoryContainer } from 'victory';
import styles from "./styles.module.scss";

import { useTranslation } from 'react-i18next';
import ChartLegend from './ChartLegend';
import ChartDetails from './ChartDetails';
import { ChartBottomMapped, ChartDataMapped, USGAStats } from './types';

type Props = {
  data: USGAStats;
};

const   ParMetricsChart: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const scoreSummary = data.scoreSummary;
  const r = 100; // static radius for web

  const parAverages = useMemo(() => {
    return [
      {
        title: scoreSummary?.par3sAverage.toFixed(2),
        subTitle: t('Par 3s'),
      },
      {
        title: scoreSummary?.par4sAverage.toFixed(2),
        subTitle: t('Par 4s'),
      },
      {
        title: scoreSummary?.par5sAverage.toFixed(2),
        subTitle: t('Par 5s'),
      },
    ] as ChartBottomMapped[];
  }, [scoreSummary, t]);

  const chartData = useMemo(() => {
    if (!scoreSummary) {
      return [];
    }

    const entries: ChartDataMapped[] = [];

    const add = (
      key: keyof typeof scoreSummary,
      title: string,
      color: string,
      index: number
    ) => {
      const percent = scoreSummary[key];
      entries.push({
        x: `${percent}%`,
        y: Math.sqrt(percent / 10) * r,
        color,
        title,
        value: `${percent}%`,
        index,
      });
    };

    add('birdiesOrBetterPercent', t('Birdie or Better'), '#E54500', 1);
    add('parsPercent', t('Par'), '#039BE5', 2);
    add('bogeysPercent', t('Bogey'), '#FFA000', 3);
    add('doubleBogeysPercent', t('Double Bogeys'), '#5E35B1', 4);
    add('tripleBogeysOrWorsePercent', t('Triple Bogeys or Higher'), '#43A047', 5);

    return entries.sort((a, b) => a.index - b.index);
  }, [scoreSummary, t]);

  return (
    <div className={styles.metrics_chart_container}>
      <div className={styles.chart_container}>
        <VictoryPie
          animate={false}
          height={200}
          width={200}
          padAngle={1}
          innerRadius={85}
          containerComponent={<VictoryContainer responsive={false} />}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
            },
            labels: {
              display: 'none',
            },
          }}
          data={chartData}
          padding={0}
        />
        <div className={styles.center_max_label}>
          <div className={styles.percent}>{scoreSummary?.parsOrBetter}%</div>
          <div className={styles.label}>{t('Par or Better')}</div>
        </div>
      </div>
      <ChartLegend data={chartData} />
      <ChartDetails data={parAverages} />
    </div>
  );
};

export default ParMetricsChart;
